import { useAuth } from '@omnipresentgroup/auth';
import { AUTH0_ROLES } from '@omnipresentgroup/constants';
import { useEmployee } from '@omnipresentgroup/domains';
import type { Employee } from '@omnipresentgroup/types';
import { useMemo } from 'react';

import { getPermissionsOnEmployee } from './getPermissionsOnEmployee';
import type { PermissionsByResource, ResourceType } from './types';

export const useEmployeePermissions = <T extends ResourceType>({
  employeeId,
  resourceType,
  omnipresentTenantId,
}: {
  employeeId?: string;
  resourceType: T;
  omnipresentTenantId?: string;
}): {
  employee: Employee | undefined;
  permissions: PermissionsByResource[ResourceType];
} => {
  const { user } = useAuth();

  const userRoles = useMemo(
    () => user?.[AUTH0_ROLES]?.map((role) => role.type) || [],
    [user],
  );

  const { data: employee } = useEmployee({
    id: employeeId,
    enabled: !!employeeId,
  });

  return useMemo(
    () => ({
      employee,
      permissions: getPermissionsOnEmployee({
        userRoles,
        employee,
        resourceType,
        omnipresentTenantId,
      }),
    }),
    [userRoles, employee, resourceType, omnipresentTenantId],
  );
};
